import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Button, Dialog, useMediaQuery } from "@material-ui/core";
import CourseCampaignContent, { useCoursePopupScale } from "./CourseCampaignContent";
import campaignService, { createCampaignCancellation } from "../../services/courseCampaignService";
import { contactValidationMessages, formatContactPhone, validateContact } from "./course-campaign-validation";
import "./course-campaign.css";

const isActive = (campaign) => campaign && (!campaign.endsAt || Date.parse(campaign.endsAt) > Date.now());

export function formatCountdown(remainingMs) {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [days, hours, minutes, seconds].map((value) => String(value).padStart(2, "0")).join(":");
}

const countdownUnits = ["ימים", "שעות", "דקות", "שניות"];

function errorMessage(error) {
  const body = error.response?.data;
  if (body?.code === "RATE_LIMITED") return "נשלחו פניות רבות מדי. יש להמתין מעט ולנסות שוב.";
  if (body?.code === "VALIDATION_FAILED") return "יש לבדוק את השם ואת מספר הטלפון ולנסות שוב.";
  if (body?.code === "DELIVERY_FAILED" || body?.code === "SERVICE_UNAVAILABLE") return "לא הצלחנו לשלוח את הפנייה. הפרטים עדיין בטופס, ואפשר לנסות שוב.";
  return "לא ניתן לאשר אם הפנייה נשלחה. כדי למנוע שליחה כפולה, בדקו מול המשרד לפני שליחה נוספת. השליחה מהטופס הזה נעצרה.";
}

const definiteFailure = (failure) => ["RATE_LIMITED", "VALIDATION_FAILED", "DELIVERY_FAILED", "SERVICE_UNAVAILABLE", "CAMPAIGN_INACTIVE"].includes(failure.response?.data?.code);

// Export the session component to exercise lifecycle boundaries without Redux.
export function CourseCampaignSession({ token, service = campaignService, pageReady = true }) {
  const popupScale = useCoursePopupScale();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [campaign, setCampaign] = useState(null);
  const [open, setOpen] = useState(false);
  const [contactName, setName] = useState("");
  const [contactPhone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [remainingMs, setRemainingMs] = useState(null);
  const mounted = useRef(false);
  const cancellation = useRef(null);
  const pending = useRef(false);
  const revision = useRef(0);
  const campaignRef = useRef(null);
  const nameInput = useRef(null);
  const banner = useRef(null);

  const alive = () => mounted.current && localStorage.getItem("token") === token;
  const updateCampaign = (next) => {
    campaignRef.current = next;
    setCampaign(next);
  };
  const expire = () => {
    setRemainingMs(null);
    updateCampaign(null);
    setOpen(false);
  };
  const freshForm = () => {
    setName("");
    setPhone("");
    setSuccess(false);
    setUncertain(false);
    setError("");
    setFieldErrors({});
    setOpen(true);
  };

  useEffect(() => {
    mounted.current = true;
    cancellation.current = createCampaignCancellation();
    service.current(cancellation.current.token).then(({ campaign: available }) => {
      if (!alive()) return;
      updateCampaign(isActive(available) ? available : null);
    }).catch(() => {
      // Campaign availability must not interrupt the application or login.
    });
    let refreshing = false;
    const refresh = async () => {
      if (!alive() || document.visibilityState === "hidden" || pending.current || refreshing) return;
      refreshing = true;
      const startedAtRevision = revision.current;
      try {
        const { campaign: available } = await service.current(cancellation.current.token);
        if (!alive() || pending.current || revision.current !== startedAtRevision) return;
        const next = isActive(available) ? available : null;
        if (!next || next.id !== campaignRef.current?.id) {
          setOpen(false);
          setName("");
          setPhone("");
          setSuccess(false);
          setError("");
        }
        // Refreshes never open the form or overwrite contact drafts.
        updateCampaign(next);
      } catch (_) {
        // Temporary polling failure leaves normal BTAX use and current input intact.
      } finally {
        refreshing = false;
      }
    };
    const refreshTimer = setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      mounted.current = false;
      cancellation.current.cancel();
      clearInterval(refreshTimer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
    // The keyed owner remounts for every identity/token change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!campaign?.courseStartsAt) {
      setRemainingMs(null);
      return undefined;
    }
    let timer;
    const check = () => {
      clearTimeout(timer);
      const remaining = Date.parse(campaign.courseStartsAt) - Date.now();
      if (remaining <= 0 || !Number.isFinite(remaining)) setRemainingMs(null);
      else {
        setRemainingMs(remaining);
        timer = setTimeout(check, Math.min(remaining, 1000));
      }
    };
    check();
    window.addEventListener("focus", check);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", check);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.courseStartsAt]);

  useEffect(() => {
    if (!campaign?.endsAt) return undefined;
    let timer;
    const check = () => {
      clearTimeout(timer);
      const remaining = Date.parse(campaign.endsAt) - Date.now();
      if (remaining <= 0 || !Number.isFinite(remaining)) expire();
      else timer = setTimeout(check, Math.min(remaining, 60000));
    };
    check();
    window.addEventListener("focus", check);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", check);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.endsAt]);

  useEffect(() => {
    if (open && !busy && !success) nameInput.current?.focus();
    // Focus once when the form opens; request failures should retain field focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleInactive = (failure) => {
    if (failure.response?.data?.code === "CAMPAIGN_INACTIVE") {
      expire();
      return true;
    }
    return false;
  };

  const manualOpen = async () => {
    if (pending.current || !pageReady) return;
    revision.current += 1;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const { campaign: available } = await service.current(cancellation.current.token);
      if (!alive()) return;
      if (!isActive(available)) { expire(); return; }
      updateCampaign(available);
      freshForm();
    } catch (failure) {
      if (alive() && !handleInactive(failure)) setError("לא הצלחנו לפתוח את הטופס. אפשר לנסות שוב בהמשך.");
    } finally {
      if (alive()) setBusy(false);
      pending.current = false;
    }
  };

  const close = () => {
    if (pending.current) return;
    const current = campaignRef.current;
    if (!isActive(current)) { expire(); return; }
    setError("");
    setOpen(false);
    setName("");
    setPhone("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (pending.current || success || uncertain) return;
    const current = campaignRef.current;
    if (!isActive(current)) { expire(); return; }
    const validation = validateContact(contactName, contactPhone);
    setFieldErrors(validation.errors);
    setError("");
    if (Object.keys(validation.errors).length) {
      document.getElementById(validation.errors.contactName ? "course-contact-name" : "course-contact-phone")?.focus();
      return;
    }
    pending.current = true;
    revision.current += 1;
    setBusy(true);
    try {
      const result = await service.submit(current.id, {
        contactName: contactName.trim(), contactPhone: validation.phone
      }, cancellation.current.token);
      if (!alive()) return;
      if (result?.notificationStatus !== "accepted") throw new Error("Unconfirmed delivery outcome");
      setName("");
      setPhone("");
      setSuccess(true);
    } catch (failure) {
      if (!alive() || handleInactive(failure)) return;
      if (!definiteFailure(failure)) setUncertain(true);
      const serverFields = failure.response?.data?.fieldErrors || {};
      const nextFieldErrors = {
        ...(serverFields.contactName ? { contactName: contactValidationMessages.nameInvalid } : {}),
        ...(serverFields.contactPhone ? { contactPhone: contactValidationMessages.phoneInvalid } : {})
      };
      setFieldErrors(nextFieldErrors);
      setError(failure.response?.data?.code === "VALIDATION_FAILED" && Object.keys(nextFieldErrors).length ? "" : errorMessage(failure));
    } finally {
      if (alive()) setBusy(false);
      pending.current = false;
    }
  };

  const changeContact = (field, value) => {
    if (field === "contactName") setName(value);
    else setPhone(value);
    if (fieldErrors[field]) {
      const nextError = field === "contactPhone" ? undefined : validateContact(value, contactPhone).errors.contactName;
      setFieldErrors((current) => ({ ...current, [field]: nextError }));
    }
  };

  if (!campaign) return null;
  const countdownValues = remainingMs === null ? [] : formatCountdown(remainingMs).split(":");
  return (
    <div className="course-campaign" dir="rtl">
      <div className="course-campaign-promo">
        <Button ref={banner} data-testid="course-campaign-banner" className={`course-campaign-banner${remainingMs !== null ? " course-campaign-banner-connected" : ""}`} onClick={manualOpen} disabled={!pageReady || (busy && !open)} aria-haspopup="dialog" aria-label={campaign.title} title={campaign.title}>
          <span className="course-campaign-banner-icon" aria-hidden="true">🎓</span>
          <span className="course-campaign-banner-title" aria-hidden="true">{campaign.title}</span>
          <span className="course-campaign-banner-arrow" aria-hidden="true">‹</span>
        </Button>
        {remainingMs !== null && <div className="course-campaign-countdown" data-testid="course-campaign-countdown" role="timer" aria-label={`הקורס מתחיל בעוד ${formatCountdown(remainingMs)}`}>
          <span className="course-campaign-countdown-label">מתחיל בעוד</span>
          <span className="course-campaign-countdown-value" dir="ltr">
            {countdownValues.map((value, index) => <React.Fragment key={countdownUnits[index]}>
              {index > 0 && <span className="course-campaign-countdown-separator" aria-hidden="true">:</span>}
              <span className="course-campaign-countdown-segment">
                <strong>{value}</strong>
                <small>{countdownUnits[index]}</small>
              </span>
            </React.Fragment>)}
          </span>
        </div>}
      </div>
      {!open && error && <p className="course-campaign-banner-error" role="alert">{error}</p>}
      <Dialog open={open} onClose={close} maxWidth={false} aria-labelledby="course-campaign-title" aria-describedby="course-campaign-description"
        transitionDuration={reducedMotion ? 0 : { enter: 360, exit: 180 }}
        PaperProps={{ dir: "rtl", className: "course-campaign-dialog", "data-testid": "course-campaign-dialog", style: { "--course-popup-scale": popupScale } }}
        TransitionProps={{ onEntered: () => nameInput.current?.focus(), onExited: () => banner.current?.focus() }}>
        <form className="course-campaign-design" onSubmit={submit} noValidate aria-busy={busy}>
          <CourseCampaignContent success={success} busy={busy} contactName={contactName} contactPhone={contactPhone}
            uncertain={uncertain} fieldErrors={fieldErrors} error={error} nameInput={nameInput}
            onNameChange={(event) => changeContact("contactName", event.target.value)}
            onPhoneChange={(event) => changeContact("contactPhone", event.target.value.replace(/[^0-9+ ().-]/g, ""))}
            onPhoneBlur={() => setPhone(formatContactPhone(contactPhone))}
            onClose={close} />
        </form>
      </Dialog>
    </div>
  );
}

function CourseCampaign({ identity, pageReady }) {
  const [storageRevision, setStorageRevision] = useState(0);
  useEffect(() => {
    const changed = (event) => {
      if (!event.key || ["token", "email", "belongToCardNum"].includes(event.key)) setStorageRevision((value) => value + 1);
    };
    window.addEventListener("storage", changed);
    return () => window.removeEventListener("storage", changed);
  }, []);
  const token = localStorage.getItem("token");
  return token ? <CourseCampaignSession key={identity + ":" + token + ":" + storageRevision} token={token} pageReady={pageReady} /> : null;
}

export default connect((state) => ({ identity: [state.loggedinReducer.email, state.loggedinReducer.belongToCardNum].join(":") }))(CourseCampaign);
